import { DEFAULT_MIN_GPS_ACCURACY_METERS } from '@/utils/factoryVerificationConfig.util';
import { isDeviceLocationPreciseEnough } from '@/utils/factoryVerificationGps.util';
import {
  logFactoryVerificationDebug,
  logFactoryVerificationError,
} from '@/utils/factoryVerificationLogger.util';
import { getCurrentPosition, watchBestPosition } from '@/utils/geolocation';
import { GpsCaptureSource, GeolocationPermissionState } from '@/types/LocationVerification.type';
import { readClientUserAgentAtCapture } from '@/utils/readClientUserAgentAtCapture';
import { readGeolocationPermissionStateAtCapture } from '@/utils/readGeolocationPermissionStateAtCapture';
import { readNetworkConnectionAtCapture } from '@/utils/readNetworkConnectionAtCapture';

export type DeviceLocationFix = {
  latitude: number;
  longitude: number;
  accuracyMetres: number | null;
  altitude: number | null;
  altitudeAccuracy: number | null;
  heading: number | null;
  speed: number | null;
  gpsCaptureSource: GpsCaptureSource;
  /** True when accuracy meets min-gps-accuracy-meters at capture time. */
  gpsAccuracyPreciseEnough: boolean;
  connectionType: string | null;
  connectionEffectiveType: string | null;
  geolocationPermissionState: GeolocationPermissionState | null;
  clientUserAgent: string | null;
  gpsCapturedAt: string;
};

type CaptureBestDeviceLocationParams = {
  maxWatchMs?: number;
  minAccuracyMetres?: number;
  /** Max age (ms) for the low-accuracy fallback fix. Use 0 for photo evidence. */
  maxFallbackAgeMs?: number;
};

function toNullableFiniteNumber(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

async function buildDeviceLocationFix(
  position: GeolocationPosition,
  gpsCaptureSource: GpsCaptureSource,
  minAccuracyMetres: number
): Promise<DeviceLocationFix> {
  const { latitude, longitude, accuracy, altitude, altitudeAccuracy, heading, speed } =
    position.coords;
  const { connectionType, connectionEffectiveType } = readNetworkConnectionAtCapture();
  const geolocationPermissionState = await readGeolocationPermissionStateAtCapture();
  const clientUserAgent = readClientUserAgentAtCapture();
  const fix: DeviceLocationFix = {
    latitude,
    longitude,
    accuracyMetres: toNullableFiniteNumber(accuracy),
    altitude: toNullableFiniteNumber(altitude),
    altitudeAccuracy: toNullableFiniteNumber(altitudeAccuracy),
    heading: toNullableFiniteNumber(heading),
    speed: toNullableFiniteNumber(speed),
    gpsCaptureSource,
    gpsAccuracyPreciseEnough: false,
    connectionType,
    connectionEffectiveType,
    geolocationPermissionState,
    clientUserAgent,
    gpsCapturedAt: new Date().toISOString(),
  };
  fix.gpsAccuracyPreciseEnough = isDeviceLocationPreciseEnough(fix, minAccuracyMetres);
  return fix;
}

/**
 * Captures the best available device GPS fix with a short watch window and fallback.
 */
export async function captureBestDeviceLocation(
  params: CaptureBestDeviceLocationParams = {}
): Promise<DeviceLocationFix | null> {
  if (!navigator?.geolocation) {
    logFactoryVerificationError('gps.unavailable', { reason: 'geolocationNotSupported' });
    return null;
  }
  const maxWatchMs = params.maxWatchMs ?? 12_000;
  const minAccuracyMetres = params.minAccuracyMetres ?? DEFAULT_MIN_GPS_ACCURACY_METERS;
  const maxFallbackAgeMs = params.maxFallbackAgeMs ?? 60_000;
  logFactoryVerificationDebug('gps.captureStarted', {
    maxWatchMs,
    minAccuracyMetres,
    maxFallbackAgeMs,
  });
  try {
    const bestPrecise = await watchBestPosition({
      maxWatchMs,
      stopWhenAccuracyLTE: minAccuracyMetres,
      options: {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 10_000,
      },
    });
    const usedPreciseWatch = Boolean(bestPrecise);
    const position =
      bestPrecise ??
      (await getCurrentPosition({
        enableHighAccuracy: false,
        maximumAge: maxFallbackAgeMs,
        timeout: 8_000,
      }));
    const fix = await buildDeviceLocationFix(
      position,
      usedPreciseWatch ? 'HIGH_ACCURACY_WATCH' : 'LOW_ACCURACY_FALLBACK',
      minAccuracyMetres
    );
    logFactoryVerificationDebug('gps.captureSucceeded', {
      latitude: fix.latitude,
      longitude: fix.longitude,
      accuracyMetres: fix.accuracyMetres,
      altitude: fix.altitude,
      altitudeAccuracy: fix.altitudeAccuracy,
      heading: fix.heading,
      speed: fix.speed,
      gpsCaptureSource: fix.gpsCaptureSource,
      gpsAccuracyPreciseEnough: fix.gpsAccuracyPreciseEnough,
      connectionType: fix.connectionType,
      connectionEffectiveType: fix.connectionEffectiveType,
      geolocationPermissionState: fix.geolocationPermissionState,
      clientUserAgent: fix.clientUserAgent,
      usedPreciseWatch,
    });
    return fix;
  } catch (error) {
    logFactoryVerificationError('gps.captureFailed', {
      error: error instanceof Error ? error.message : String(error),
    });
    return null;
  }
}
